/**
 * About — short, calm page conveying why Alouvya exists.
 *
 * Per the product-copy spec §9 SEO table, this page exists at /about.
 * Brand voice rules: warm, honest, no false claims, no surveillance vocab.
 */
import { Link } from "react-router-dom";
import MarketingNav from "../components/MarketingNav";
import MarketingFooter from "../components/MarketingFooter";
import MetaTags from "../components/MetaTags";
import { useI18n } from "../lib/i18n";

export default function About() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-white" data-testid="about-screen">
      <MetaTags
        title={t("marketing.about.meta_title")}
        description={t("marketing.about.meta_description")}
      />
      <MarketingNav />
      <main className="pt-32 sm:pt-40 pb-24">
        <section className="mx-auto max-w-3xl px-6 sm:px-10">
          <span className="eyebrow">{t("marketing.about.eyebrow")}</span>
          <h1 className="mt-4 font-display text-4xl sm:text-6xl tracking-tight leading-[1.05] text-[#1A1B2E]">
            {t("marketing.about.headline_lead")}
            <br />
            <span className="italic font-light text-[#1F3A2A]">
              {t("marketing.about.headline_italic")}
            </span>
          </h1>

          <p className="mt-10 text-lg text-[#1A1B2E] leading-relaxed font-light">
            {t("marketing.about.p1")}
          </p>

          <p className="mt-6 text-lg text-[#6B7280] leading-relaxed">
            {t("marketing.about.p2")}
          </p>

          <p className="mt-6 text-lg text-[#6B7280] leading-relaxed">
            {t("marketing.about.p3")}
          </p>

          <h2 className="mt-16 font-display text-3xl text-[#1A1B2E] tracking-tight">
            {t("marketing.about.beliefs_title")}
          </h2>
          <ul className="mt-6 space-y-4 text-[#1A1B2E] leading-relaxed">
            {[
              t("marketing.about.belief_1"),
              t("marketing.about.belief_2"),
              t("marketing.about.belief_3"),
              t("marketing.about.belief_4"),
              t("marketing.about.belief_5"),
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <span
                  aria-hidden
                  className="mt-2.5 h-1 w-6 bg-[#1F3A2A] rounded-full shrink-0"
                />
                <span className="text-base">{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-16 rounded-3xl bg-[#F7F8FC] border border-[#EEF0F4] p-8">
            <div className="eyebrow">{t("marketing.about.working_with_us_eyebrow")}</div>
            <p className="mt-3 text-[#1A1B2E]">
              {t("marketing.about.working_with_us_body")}
            </p>
            <Link
              to="/demo"
              data-testid="about-demo-link"
              className="mt-6 inline-flex items-center justify-center px-6 py-3 rounded-full bg-[#1F3A2A] text-white text-sm font-semibold hover:bg-[#132C17] transition-colors"
            >
              {t("marketing.about.book_conversation")}
            </Link>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
